import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import clinicAPI from "../../../services/clinicApi";
import ClubLifePostCard from "../common/ClubLifePostCard";
import ClubLifeComposer from "./ClubLifeComposer";
import ClientPostManageModals from "./ClientPostManageModals";
import { useClubLifeInteractions } from "../../../hooks/useClubLifeInteractions";
import { useClientPostManage } from "../../../hooks/useClientPostManage";
import { useClientRefresh } from "../../../hooks/useClientRefresh";
import useClubPortalBranding from "../../../hooks/useClubPortalBranding";
import {
  mediaUrl,
  TRAINER_COLOR_PRESETS,
} from "../../../utils/clientHeroTheme";
import { Avatar, Button, Skeleton, useToast } from "../../ui";
import s from "./clientApp.module.css";

function themeStyle(themeId) {
  const preset = TRAINER_COLOR_PRESETS.find((p) => p.id === themeId);
  if (!preset) return {};
  return {
    "--member-accent": preset.color,
    "--ca-accent": preset.color,
  };
}

function formatRank(card) {
  if (!card?.rank_place) return "—";
  if (card.rank_total) return `${card.rank_place} из ${card.rank_total}`;
  return `${card.rank_place} место`;
}

export default function ClientMemberProfile({ profileTab = false }) {
  const { userId: paramUserId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const myId = useSelector((state) => state.auth.profile?.id);
  const userId = profileTab ? myId : paramUserId;
  const [profile, setProfile] = useState(null);
  const [tab, setTab] = useState("wall");
  const [wallPosts, setWallPosts] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [postsLoading, setPostsLoading] = useState(true);
  const [followBusy, setFollowBusy] = useState(false);
  const coverInputRef = useRef(null);
  const { heroStyle } = useClubPortalBranding("ca");

  const isSelf = profile?.is_self || String(userId) === String(myId);
  const clubCard = profile?.club_card;

  const {
    commentBusy,
    openComments,
    commentsByPost,
    toggleLike,
    loadComments,
    addComment,
  } = useClubLifeInteractions(tab === "wall" ? setWallPosts : setAchievements);
  const postManage = useClientPostManage(tab === "wall" ? setWallPosts : setAchievements);

  const loadProfile = useCallback(async () => {
    if (!userId) return;
    try {
      const data = await clinicAPI.getSocialMemberProfile(userId);
      setProfile(data.profile || null);
    } catch (err) {
      setProfile(null);
      const status = err?.response?.status;
      const msg = err?.response?.data?.error;
      if (status === 403) {
        toast.error("Профиль закрыт");
      } else if (status === 404) {
        toast.error(msg || "Участник не найден");
      } else if (msg) {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [userId, toast]);

  const loadPosts = useCallback(async () => {
    if (!userId) return;
    setPostsLoading(true);
    try {
      if (tab === "wall") {
        const data = await clinicAPI.getSocialMemberWall(userId);
        setWallPosts(data.posts || []);
      } else {
        const data = await clinicAPI.getSocialMemberAchievements(userId);
        setAchievements(data.posts || []);
      }
    } catch {
      if (tab === "wall") setWallPosts([]);
      else setAchievements([]);
    } finally {
      setPostsLoading(false);
    }
  }, [tab, userId]);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    loadProfile();
  }, [loadProfile, userId]);

  useEffect(() => {
    if (!userId || !profile) return;
    loadPosts();
  }, [profile, loadPosts, userId]);

  useClientRefresh(() => {
    loadProfile();
    loadPosts();
  });

  const posts = tab === "wall" ? wallPosts : achievements;
  const shellStyle = useMemo(
    () => themeStyle(profile?.profile_theme),
    [profile?.profile_theme],
  );

  const primaryGroup =
    clubCard?.primary_group ||
    profile?.groups?.[0]?.name ||
    null;

  const toggleFollow = async () => {
    if (!profile || isSelf) return;
    setFollowBusy(true);
    try {
      const fn = profile.is_following
        ? clinicAPI.unfollowSocialMember
        : clinicAPI.followSocialMember;
      const r = await fn(userId);
      setProfile((prev) =>
        prev
          ? {
              ...prev,
              is_following: r.is_following,
              followers_count: r.followers_count,
            }
          : prev,
      );
    } catch {
      toast.error("Не удалось обновить подписку");
    } finally {
      setFollowBusy(false);
    }
  };

  const writeToMember = () => {
    const firstName = (profile?.full_name || "").trim().split(/\s+/)[0];
    navigate(`/app/chat/member/${userId}`, {
      state: {
        draft: firstName ? `${firstName}, ` : "",
        name: profile?.full_name || "Участник",
      },
    });
  };

  const handleWallPublished = useCallback(() => {
    loadPosts();
    loadProfile();
  }, [loadPosts, loadProfile]);

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const r = await clinicAPI.uploadSocialCover(file);
      setProfile((prev) =>
        prev ? { ...prev, profile_cover_url: r.url } : prev,
      );
    } catch {
      toast.error("Не удалось загрузить обложку");
    }
    e.target.value = "";
  };

  if (!userId || loading) {
    return <Skeleton height={320} style={{ borderRadius: 0 }} />;
  }

  if (!profile) {
    return (
      <div className={s.socialEmpty}>
        <span>🔒</span>
        <p>Профиль недоступен</p>
        <Button type="button" onClick={() => navigate(-1)}>
          Назад
        </Button>
      </div>
    );
  }

  const coverUrl = mediaUrl(profile.profile_cover_url);

  return (
    <div className={s.memberProfilePage} style={shellStyle}>
      <div className={s.memberCoverWrap}>
        <div className={s.memberCover}>
          <div
            className={s.memberCoverBg}
            style={
              coverUrl
                ? { backgroundImage: `url(${coverUrl})` }
                : heroStyle
            }
          />
          {coverUrl ? <div className={s.memberCoverGlow} aria-hidden /> : null}
        {!profileTab ? (
          <button
            type="button"
            className={s.memberBackBtn}
            onClick={() => navigate(-1)}
            aria-label="Назад"
          >
            ←
          </button>
        ) : null}
        {isSelf ? (
          <>
            <button
              type="button"
              className={
                profileTab
                  ? s.memberCoverEdit
                  : `${s.memberCoverEdit} ${s.memberCoverEditAlt}`
              }
              onClick={() => coverInputRef.current?.click()}
            >
              Обложка
            </button>
            <input
              ref={coverInputRef}
              type="file"
              accept="image/*"
              hidden
              onChange={uploadCover}
            />
          </>
        ) : null}
        {profileTab ? (
          <Link
            to="/app/profile/account"
            className={s.memberSettingsBtn}
            aria-label="Настройки"
          >
            ⚙
          </Link>
        ) : null}
        </div>
      </div>

      <div className={s.memberBody}>
        <div className={s.memberIgRow}>
          <Avatar
            name={profile.full_name}
            src={mediaUrl(profile.photo_url)}
            size="lg"
            className={s.memberAvatarIg}
          />
          <div className={s.memberIgStats} aria-label="Статистика">
            <div className={s.memberIgStat}>
              <strong>{profile.wall_posts_count ?? 0}</strong>
              <span>записи</span>
            </div>
            <div className={s.memberIgStat}>
              <strong>{profile.followers_count ?? 0}</strong>
              <span>подписчики</span>
            </div>
            <div className={s.memberIgStat}>
              <strong>{profile.following_count ?? 0}</strong>
              <span>подписки</span>
            </div>
          </div>
        </div>

        <div className={s.memberIdentity}>
          <h1 className={s.memberNameIg}>{profile.full_name}</h1>
          {profile.profile_bio ? (
            <p className={s.memberBioIg}>{profile.profile_bio}</p>
          ) : null}
        </div>

        <div className={s.memberClubStrip}>
          <div className={s.memberClubTile}>
            <span className={s.memberClubTileLabel}>Группа</span>
            <strong className={s.memberClubTileValue}>
              {primaryGroup || "—"}
            </strong>
          </div>
          {isSelf ? (
            <Link to="/app/stats" className={s.memberClubTile}>
              <span className={s.memberClubTileLabel}>Рейтинг</span>
              <strong className={s.memberClubTileValue}>
                {formatRank(clubCard)}
              </strong>
              {clubCard?.attendance_pct != null ? (
                <small className={s.memberClubTileHint}>
                  {clubCard.attendance_pct}% явка
                </small>
              ) : null}
            </Link>
          ) : (
            <div className={s.memberClubTile}>
              <span className={s.memberClubTileLabel}>Рейтинг</span>
              <strong className={s.memberClubTileValue}>
                {formatRank(clubCard)}
              </strong>
              {clubCard?.attendance_pct != null ? (
                <small className={s.memberClubTileHint}>
                  {clubCard.attendance_pct}% явка
                </small>
              ) : null}
            </div>
          )}
          {isSelf ? (
            <Link to="/app/profile/account/club" className={s.memberClubTile}>
              <span className={s.memberClubTileLabel}>Абонемент</span>
              <strong className={s.memberClubTileValue}>
                {clubCard?.membership_label || "—"}
              </strong>
            </Link>
          ) : (
            <div className={s.memberClubTile}>
              <span className={s.memberClubTileLabel}>Достижения</span>
              <strong className={s.memberClubTileValue}>
                {profile.achievements_count ?? 0}
              </strong>
            </div>
          )}
        </div>

        <div className={s.memberBtnRow}>
          {isSelf ? (
            <>
              <Link
                to="/app/profile/account/social"
                className={s.memberBtnOutline}
              >
                Редактировать
              </Link>
              <Link to="/app/profile/account" className={s.memberBtnOutline}>
                Настройки
              </Link>
            </>
          ) : (
            <>
              <button
                type="button"
                className={
                  profile.is_following ? s.memberBtnOutline : s.memberBtnPrimary
                }
                onClick={toggleFollow}
                disabled={followBusy}
              >
                {followBusy
                  ? "…"
                  : profile.is_following
                    ? "Вы подписаны"
                    : "Подписаться"}
              </button>
              <button
                type="button"
                className={s.memberBtnOutline}
                onClick={writeToMember}
              >
                Написать
              </button>
            </>
          )}
        </div>

        <nav className={s.memberTabs} aria-label="Разделы профиля">
          <button
            type="button"
            className={tab === "wall" ? s.memberTabOn : s.memberTab}
            onClick={() => setTab("wall")}
          >
            Стена
          </button>
          <button
            type="button"
            className={tab === "achievements" ? s.memberTabOn : s.memberTab}
            onClick={() => setTab("achievements")}
          >
            Достижения
          </button>
        </nav>

        {isSelf && tab === "wall" ? (
          <div className={s.memberComposerSlot}>
            <ClubLifeComposer
              compact
              embedded
              wallMode
              onPublished={handleWallPublished}
            />
          </div>
        ) : null}

        {postsLoading ? (
          <Skeleton
            height={160}
            style={{ borderRadius: 16, margin: "12px 0" }}
          />
        ) : posts.length === 0 ? (
          <div className={s.memberFeedEmpty}>
            <span>{tab === "wall" ? "📝" : "🏆"}</span>
            <p>
              {tab === "wall"
                ? isSelf
                  ? "Пока нет записей на стене"
                  : "На стене пока нет записей"
                : "Достижений пока нет"}
            </p>
          </div>
        ) : (
          <div className={s.memberPosts}>
            {posts.map((post) => (
              <ClubLifePostCard
                key={post.id}
                post={post}
                mode="client"
                currentUserId={isSelf ? myId : null}
                onOpenMenu={isSelf ? postManage.openMenu : undefined}
                onLike={toggleLike}
                onLoadComments={loadComments}
                commentsOpen={openComments === post.id}
                comments={commentsByPost[post.id] || []}
                onComment={addComment}
                commentBusy={commentBusy}
              />
            ))}
          </div>
        )}
      </div>
      {isSelf ? <ClientPostManageModals {...postManage} /> : null}
    </div>
  );
}
